import { useEffect } from 'react';

/**
 * TechDocs addon that makes the sidebar navigation collapsible.
 * Injects CSS and click handlers to toggle nested <ul> sections.
 */
export const CollapsibleNavAddon = () => {
  useEffect(() => {
    const STYLE_ID = 'techdocs-collapsible-nav';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        /* Hide nested lists by default */
        [class*="techdocs-nav"] ul ul,
        [class*="TechDocsNav"] ul ul,
        nav[data-testid="techdocs-nav"] ul ul,
        .md-nav__list .md-nav__list {
          display: none;
          padding-left: 12px;
        }
        /* Show when parent has 'open' class */
        [class*="techdocs-nav"] li.nav-open > ul,
        [class*="TechDocsNav"] li.nav-open > ul,
        nav[data-testid="techdocs-nav"] li.nav-open > ul,
        .md-nav__item.nav-open > .md-nav__list {
          display: block;
        }
        /* Arrow indicator for expandable items */
        [class*="techdocs-nav"] li.nav-has-children > a::before,
        [class*="techdocs-nav"] li.nav-has-children > span::before,
        [class*="TechDocsNav"] li.nav-has-children > a::before,
        [class*="TechDocsNav"] li.nav-has-children > span::before,
        nav[data-testid="techdocs-nav"] li.nav-has-children > a::before,
        nav[data-testid="techdocs-nav"] li.nav-has-children > span::before {
          content: '▸ ';
          display: inline;
          transition: transform 0.2s;
        }
        [class*="techdocs-nav"] li.nav-has-children.nav-open > a::before,
        [class*="techdocs-nav"] li.nav-has-children.nav-open > span::before,
        [class*="TechDocsNav"] li.nav-has-children.nav-open > a::before,
        [class*="TechDocsNav"] li.nav-has-children.nav-open > span::before,
        nav[data-testid="techdocs-nav"] li.nav-has-children.nav-open > a::before,
        nav[data-testid="techdocs-nav"] li.nav-has-children.nav-open > span::before {
          content: '▾ ';
        }
        /* Clickable cursor for parent items */
        [class*="techdocs-nav"] li.nav-has-children > a,
        [class*="techdocs-nav"] li.nav-has-children > span,
        [class*="TechDocsNav"] li.nav-has-children > a,
        [class*="TechDocsNav"] li.nav-has-children > span,
        nav[data-testid="techdocs-nav"] li.nav-has-children > a,
        nav[data-testid="techdocs-nav"] li.nav-has-children > span {
          cursor: pointer;
        }
      `;
      document.head.appendChild(style);
    }

    const applyCollapsible = () => {
      // Find the TechDocs nav container
      const navContainers = document.querySelectorAll(
        '[class*="techdocs-nav"], [class*="TechDocsNav"], nav[data-testid="techdocs-nav"]',
      );
      // Fallback: any sidebar nav with nested lists
      const containers =
        navContainers.length > 0
          ? navContainers
          : document.querySelectorAll('nav ul ul')?.length
            ? [document.querySelector('nav')!].filter(Boolean)
            : [];

      containers.forEach(nav => {
        const items = nav.querySelectorAll('li');
        items.forEach(li => {
          const nestedUl = li.querySelector(':scope > ul');
          if (!nestedUl) return;
          if (li.classList.contains('nav-has-children')) return;

          li.classList.add('nav-has-children');

          const clickTarget = li.querySelector(':scope > a, :scope > span');
          if (clickTarget) {
            clickTarget.addEventListener('click', (e: Event) => {
              // Only toggle if the link is a section header (href is # or same page)
              const anchor = clickTarget as HTMLAnchorElement;
              const href = anchor.getAttribute('href');
              if (
                !href ||
                href === '#' ||
                href === '' ||
                nestedUl.querySelector('a')
              ) {
                e.preventDefault();
                li.classList.toggle('nav-open');
              }
            });
          }
        });
      });
    };

    // Run after TechDocs content loads (it renders async in shadow DOM / iframe)
    const timer = setInterval(applyCollapsible, 1000);
    applyCollapsible();

    // Also observe DOM changes for SPA navigation
    const observer = new MutationObserver(() => applyCollapsible());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(timer);
      observer.disconnect();
    };
  }, []);

  return null;
};
